import { Button, Icon, Stack, styled } from '@qcx/ui';
import { ComponentProps, useCallback, useEffect } from 'react';
import { UserService } from '@atreus/data';
import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-light-svg-icons';
import { useAuthModalStore } from '@kratos/auth';
import { useNewsStore } from '../../news.store';

export type LikeDislikeButtonProps = ComponentProps<typeof StyledButton>;

export interface LikeDislikeButtonsProps {
  slug: string;
}

// TODO: Refactor this component, starting with the backend. The implementation was rushed due to time constraints and it wasn't a high priority at the moment. A detailed review is necessary to enhance its structure and efficiency, ensuring that the backend logic is optimized before making changes to the frontend.

export const LikeDislikeButtons = ({ slug }: LikeDislikeButtonProps) => {
  const toggleIsLikeActive = useNewsStore(s => s.toggleIsLikeActive);
  const toggleIsDislikeActive = useNewsStore(s => s.toggleIsDislikeActive);
  const toggleAuthModal = useAuthModalStore(s => s.toggle);
  const toggleSigninModal = useCallback(() => {
    toggleAuthModal('signup');
  }, [toggleAuthModal]);
  const currentUser = useNewsStore(s => s.userInfo);
  const checkArticleReaction = useNewsStore(s => s.checkArticleReaction);
  const isLikeActive = useNewsStore(s => s.isLikeActive);
  const isDislikeActive = useNewsStore(s => s.isDislikeActive);
  const likes = useNewsStore(s => s.likes);
  const dislikes = useNewsStore(s => s.dislikes);

  useEffect(() => {
    if (currentUser) {
      checkArticleReaction(slug);
    }
  }, [currentUser, checkArticleReaction, slug]);

  const handleSubmit = useCallback(
    async (type: string) => {
      let isAuthenticated = Boolean(currentUser) || false;

      if (!isAuthenticated) {
        toggleSigninModal();
        return;
      }

      try {
        const userService = new UserService({ credentials: 'include' });
        const user = await userService.getAtreusUser();
        isAuthenticated = !!user;
      } catch {
        isAuthenticated = false;
      }

      if (type === 'like') {
        toggleIsLikeActive(slug);
      } else {
        toggleIsDislikeActive(slug);
      }
    },
    [
      slug,
      toggleIsDislikeActive,
      toggleIsLikeActive,
      toggleSigninModal,
      currentUser
    ]
  );
  return (
    <Stack css={{ gap: '$4' }}>
      <LikeButton
        aria-label="Botão para curtir"
        isActive={isLikeActive}
        likes={likes}
        onClick={() => {
          handleSubmit('like');
        }}
      />
      <DislikeButton
        aria-label="Botão para não curtir"
        dislikes={dislikes}
        isActive={isDislikeActive}
        onClick={() => {
          handleSubmit('dislike');
        }}
      />
    </Stack>
  );
};

const LikeButton = ({ likes, ...props }: LikeDislikeButtonProps) => (
  <StyledButton
    {...props}
    leftIcon={
      <Icon css={{ minMaxH: 20, color: '$neutral500' }} icon={faThumbsUp} />
    }
  >
    {likes}
  </StyledButton>
);

const DislikeButton = ({ dislikes, ...props }: LikeDislikeButtonProps) => (
  <StyledButton
    {...props}
    leftIcon={
      <Icon css={{ minMaxH: 20, color: '$neutral500' }} icon={faThumbsDown} />
    }
  >
    {dislikes}
  </StyledButton>
);

const StyledButton = styled(Button, {
  borderRadius: 38,
  color: '$neutral600 !important',
  flexCenter: true,
  px: '$4 !important',

  variants: {
    isActive: {
      true: {
        bg: '$blue50',
        border: '1px solid $blue300',
        color: '$blue800 !important',
        '&:hover': {
          bg: '$blue100 !important',
          color: '$blue800 !important'
        }
      },
      false: {
        bg: '$neutral50',
        border: '1px solid $neutral50',
        color: '$neutral700',
        '&:hover': {
          bg: '$neutral200 !important',
          border: '1px solid $neutral200 !important',
          color: '$neutral700'
        }
      }
    }
  }
});
